import { ref } from "vue";
import db from "@/db";
import { syncPendingProjects } from "@/composables/network/usePendingSync";
import { wait } from "@/utils/helpers";
import workbox from "@/registerServiceWorker";
import { useMultiTabBroadcast } from "./useMultiTabBroadcast";

const REFRESH_EVENTS = {
  CLEAR_REQUESTED: "app-clear-requested",
  REFRESH_NOW: "app-refresh-now",
};

const WORKER_CLOSE_TIMEOUT_MS = 2000;
const SW_ACTIVATION_TIMEOUT_MS = 5000;
const PENDING_SYNC_TIMEOUT_MS = 10000;

let isDeletingDatabase = false;

export const isDeletedDatabaseError = (error) =>
  isDeletingDatabase && error?.name === "DatabaseClosedError";

const reloadOnDatabaseChange = () => {
  isDeletingDatabase = true;
  globalThis.location.reload();
};

export function initAppRefreshListener() {
  const multiTab = useMultiTabBroadcast();

  db.on("versionchange", reloadOnDatabaseChange);
  db.on("close", reloadOnDatabaseChange);

  multiTab.onMessage(async (data) => {
    if (multiTab.isFromSelf(data)) return;

    switch (data.type) {
      case REFRESH_EVENTS.CLEAR_REQUESTED:
        multiTab.sendAck(data.eventId);
        break;

      case REFRESH_EVENTS.REFRESH_NOW:
        globalThis.location.reload();
        break;
    }
  });
}

const waitForSwActivation = () => {
  return new Promise((resolve) => {
    if (!("serviceWorker" in navigator)) {
      resolve();
      return;
    }

    const timeoutId = setTimeout(() => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange
      );
      resolve();
    }, SW_ACTIVATION_TIMEOUT_MS);

    const onControllerChange = () => {
      clearTimeout(timeoutId);
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange
      );
      setTimeout(resolve, 300);
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      onControllerChange
    );

    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      } else if (registration.installing) {
        registration.installing.addEventListener("statechange", (e) => {
          if (e.target.state === "installed" && registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        });
      }
    });
  });
};

export function useAppRefresh() {
  const isRefreshing = ref(false);
  const multiTab = useMultiTabBroadcast();

  const performFullRefresh = async ({ syncPending = true } = {}) => {
    if (isRefreshing.value) return;
    isRefreshing.value = true;

    try {
      if (syncPending)
        await Promise.race([
          syncPendingProjects(),
          wait(PENDING_SYNC_TIMEOUT_MS),
        ]);

      // Étape 1 : prévenir les autres onglets et attendre leur ACK
      await multiTab.broadcastAndWaitForAcks(
        REFRESH_EVENTS.CLEAR_REQUESTED,
        {},
        WORKER_CLOSE_TIMEOUT_MS
      );

      // Étape 2 : supprimer la DB (les autres onglets ont acké, on peut y aller)
      isDeletingDatabase = true;
      db.close();
      await db.delete();

      // Étape 3 : vider les caches et le storage
      if ("caches" in globalThis) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }

      localStorage.clear();
      sessionStorage.clear();

      // Étape 4 : mettre à jour le service worker
      if (workbox) {
        const swActivationPromise = waitForSwActivation();
        await workbox.update();
        await swActivationPromise;
      }

      // Étape 5 : demander aux autres onglets de recharger, puis recharger
      multiTab.broadcast(REFRESH_EVENTS.REFRESH_NOW);
      globalThis.location.reload();
    } catch (error) {
      console.error("[useAppRefresh] Error during refresh:", error);
      isDeletingDatabase = false;
      isRefreshing.value = false;
      throw error;
    }
  };

  return { isRefreshing, performFullRefresh };
}
